import React, { useEffect, useMemo, useRef, useState } from "react";
import Card from "../../ui/Card";
import TowerShipmentList from "../TowerShipmentList";
import TowerSection from "../TowerSection";
import TowerZoneTable from "./TowerZoneTable";
import TowerDriverFinder, { FinderSection } from "./TowerDriverFinder";
import { isToday } from "../model/monitorAlerts";
import { DriverOpenOptions } from "../model/driverFocus";
import { searchKey } from "../model/towerFormat";
import { compareTowerDrivers, TowerDriverSort } from "../model/driverSort";
import type { DriverState, TowerDashboard, TowerDriver, TowerListedShipment } from "../../../types/controlTower";

// "Seguimiento": arranca en "En camino Flex", que es lo urgente.
type DriverFilter = "all" | "flexInTransit" | "nonFlexInTransit" | "pending" | "finished";
const FILTERS: [DriverFilter, string][] = [
  ["flexInTransit", "En camino Flex"],
  ["nonFlexInTransit", "En camino particular"],
  ["pending", "Pendientes de salida"],
  ["finished", "Finalizados"],
  ["all", "Todos"],
];
// Estado del chofer (calculado por el backend) que pide cada filtro por estado.
const FILTER_STATES: Partial<Record<DriverFilter, DriverState>> = {
  pending: "pending",
  finished: "finished",
};

// Monitoreo muestra lo de hoy: los conteos del backend (counts). Lo que sigue
// de días anteriores se ve en cada chofer como "+N" (carried). Para ver otras
// fechas está el Resumen.

// Tipo de envío en "Envíos en depósito" y "Sin cerrar en Lightdata": arranca
// en Flex, que es lo urgente; particulares y todos sirven para ver el resto.
type ShipmentType = "FLEX" | "NON_FLEX" | "all";
const SHIPMENT_TYPES: [ShipmentType, string][] = [
  ["FLEX", "Flex"],
  ["NON_FLEX", "Particulares"],
  ["all", "Todos"],
];
const ofType = (shipments: TowerListedShipment[], type: ShipmentType) =>
  shipments.filter((s) => type === "all" || s.shippingUniverse === type);

function TypeFilter({
  label,
  shipments,
  value,
  onChange,
}: {
  label: string;
  shipments: TowerListedShipment[];
  value: ShipmentType;
  onChange: (type: ShipmentType) => void;
}) {
  return (
    <div className="tower-panel-actions tower-panel-actions-start">
      <div className="tower-map-filter-group" role="group" aria-label={label}>
        {SHIPMENT_TYPES.map(([id, text]) => (
          <button
            key={id}
            type="button"
            className={`tower-map-filter${value === id ? " active" : ""}`}
            aria-pressed={value === id}
            onClick={() => onChange(id)}
          >
            {text} ({ofType(shipments, id).length})
          </button>
        ))}
      </div>
    </div>
  );
}

const ZONE_ORDER = ["NORTE", "SUR", "CABA", "OESTE"];

// Monitoreo de choferes: alertas, zonas con sus choferes, depósito, lo que
// quedó sin cerrar en Lightdata y las ubicaciones por revisar.
export default function TowerMonitor({
  data,
  date,
  nowMs,
  alertsEnabled,
  countLabel,
  scopeDescription,
  operationalDateLabel,
  onSelectDriver,
  depotRequest = 0,
}: {
  data: TowerDashboard;
  date: string;
  nowMs: number;
  alertsEnabled: boolean;
  countLabel: string;
  scopeDescription: string;
  operationalDateLabel: string;
  onSelectDriver: (driverId: string, options?: DriverOpenOptions) => void;
  // Cambia cada vez que la alerta del depósito pide mostrarlo.
  depotRequest?: number;
}) {
  const [search, setSearch] = useState("");
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [driverFilter, setDriverFilter] = useState<DriverFilter>("flexInTransit");
  const [sort, setSort] = useState<TowerDriverSort>("risk");
  const [depotType, setDepotType] = useState<ShipmentType>("FLEX");
  const [unclosedType, setUnclosedType] = useState<ShipmentType>("FLEX");

  // Día nuevo: se pliega todo. El filtro de seguimiento despliega las zonas
  // para ver el resultado sin abrirlas una por una (también al cambiar de día);
  // la búsqueda no (muestra sus resultados en una lista). El depósito y las
  // demás secciones no dependen del filtro.
  const lastDate = useRef(date);
  useEffect(() => {
    const zones = Object.fromEntries(ZONE_ORDER.map((key) => [key, driverFilter !== "all"]));
    const newDay = lastDate.current !== date;
    lastDate.current = date;
    setOpenSections((previous) => ({ ...(newDay ? {} : previous), ...zones }));
  }, [date, driverFilter]);
  const setSectionOpen = (key: string) => (open: boolean) =>
    setOpenSections((previous) => ({ ...previous, [key]: open }));
  // Despliega una sección de envíos (depósito o sin cerrar) y la muestra, con
  // todos los tipos para que se vea lo que se buscó o lo que marcó la alerta.
  const openSection = (key: FinderSection) => {
    setOpenSections((previous) => ({ ...previous, [key]: true }));
    if (key === "depot") setDepotType("all");
    if (key === "unclosed") setUnclosedType("all");
    window.requestAnimationFrame(() =>
      document.getElementById(`tower-${key}`)?.scrollIntoView({ behavior: "smooth", block: "start" }),
    );
  };
  useEffect(() => {
    if (depotRequest) openSection("depot");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depotRequest]);

  const detailsById = useMemo(() => new Map(data.drivers.map((d) => [d.id, d])), [data]);
  const filtering = driverFilter !== "all";
  // Cada fila es el chofer en una zona tal como lo manda el backend: counts de
  // hoy, carried de días anteriores y su estado.
  const matches = (driver: TowerDriver) => {
    // "Sin asignar" se ve siempre que tenga envíos.
    if (driver.kind === "unassigned") return driver.counts.total + driver.carried.total > 0;
    if (driverFilter === "all") return true;
    if (driverFilter === "flexInTransit") return driver.counts.flexInTransit > 0;
    if (driverFilter === "nonFlexInTransit") return driver.counts.nonFlexInTransit > 0;
    return driver.state === FILTER_STATES[driverFilter];
  };
  // Las filas de cada zona son un recorte; la actividad se mide con el chofer completo.
  const overallOf = (driver: TowerDriver): TowerDriver => detailsById.get(driver.id) ?? driver;
  // "Sin asignar" primero; después, el orden elegido.
  const byOrder = (a: TowerDriver, b: TowerDriver) =>
    Number(b.kind === "unassigned") - Number(a.kind === "unassigned") ||
    compareTowerDrivers(a, b, sort, nowMs, alertsEnabled, overallOf);
  // Las zonas son de choferes; lo que no tiene zona se lista por envío más abajo.
  const groups = data.zones
    .filter((z) => ZONE_ORDER.includes(z.zone))
    .sort((a, b) => ZONE_ORDER.indexOf(a.zone) - ZONE_ORDER.indexOf(b.zone))
    .map((z) => ({ ...z, rows: z.drivers, shown: z.drivers.filter(matches).sort(byOrder) }));
  const shownIds = new Set(
    groups.flatMap((z) => z.shown.filter((driver) => driver.kind === "driver").map((driver) => driver.id)),
  );
  const flexInTransit = [...data.drivers.flatMap((d) => d.shipments), ...(data.depot?.shipments || [])].filter(
    (s) => s.status === "inTransit" && s.shippingUniverse === "FLEX" && isToday(s),
  ).length;

  // Las listas de envíos también se filtran con el texto del buscador.
  const query = searchKey(search.trim());
  const matchesShipment = (s: TowerListedShipment) =>
    !query || searchKey(`${s.tracking} ${s.client} ${s.locality} ${s.postalCode} ${s.assignedTo}`).includes(query);
  const shownCount = (shown: number, total: number) => (query ? `${shown} de ${total}` : `${total}`);
  // Los envíos sin zona son de choferes: lo de hoy, como las zonas. El
  // depósito y lo sin cerrar se muestran completos.
  const reviewShipments: TowerListedShipment[] = data.drivers.flatMap((d) =>
    d.shipments
      .filter((s) => s.zone === "Sin clasificar" && isToday(s))
      .map((s) => ({ ...s, assignedTo: d.name, driverId: d.id })),
  );
  const reviewShown = reviewShipments.filter(matchesShipment);
  const depotShipments = data.depot?.shipments || [];
  const depotShown = ofType(depotShipments, depotType).filter(matchesShipment);
  const unclosedShipments = data.unclosed || [];
  const unclosedShown = ofType(unclosedShipments, unclosedType).filter(matchesShipment);

  return (
    <>
      <section id="tower-drivers" aria-label="Choferes por zona">
        <Card className="tower-board">
          <div className="tower-board-heading tower-board-heading-start">
            <div className="tower-board-badges">
              <span className="tower-flex-badge">
                Flex en camino <b>{flexInTransit}</b>
              </span>
              <span className="tower-flex-badge tower-count-badge">
                Choferes <b>{shownIds.size}</b>
              </span>
            </div>
          </div>
          <div className="tower-toolbar">
            <TowerDriverFinder
              data={data}
              search={search}
              onSearchChange={setSearch}
              alertsEnabled={alertsEnabled}
              nowMs={nowMs}
              onSelectDriver={(driverId) => onSelectDriver(driverId)}
              onOpenSection={openSection}
            />
            <label>
              Seguimiento
              <select value={driverFilter} onChange={(e) => setDriverFilter(e.target.value as DriverFilter)}>
                {FILTERS.map(([id, label]) => (
                  <option key={id} value={id}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Ordenar
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as TowerDriverSort)}
                title={
                  sort === "risk"
                    ? "Primero las alertas (críticas, leves y sin entregas); después, más FLEX en camino."
                    : undefined
                }
              >
                <option value="risk">Por riesgo FLEX</option>
                <option value="flexInTransit">Más FLEX en camino</option>
                <option value="inactivity">Mayor inactividad</option>
                <option value="progress">Mayor progreso</option>
                <option value="name">Nombre</option>
              </select>
            </label>
          </div>

          {groups.map((group) => (
            <TowerZoneTable
              key={group.zone}
              zone={group.zone}
              rows={group.rows}
              shown={group.shown}
              hasDrivers={group.drivers.length > 0}
              filtering={filtering}
              activeKind={
                driverFilter === "flexInTransit" ? "flex" : driverFilter === "nonFlexInTransit" ? "nonFlex" : "all"
              }
              open={openSections[group.zone] || false}
              onToggle={setSectionOpen(group.zone)}
              countLabel={countLabel}
              scopeDescription={scopeDescription}
              detailsById={detailsById}
              alertsEnabled={alertsEnabled}
              nowMs={nowMs}
              onSelectDriver={onSelectDriver}
            />
          ))}

          <TowerSection
            id="tower-depot"
            className="tower-zone-depot"
            open={openSections.depot || false}
            onToggle={setSectionOpen("depot")}
            title="Envíos en depósito"
            subtitle="Larrazábal · Bermúdez"
            help="Asignados a los usuarios del depósito (Larrazábal, Bermúdez y sus variantes) o «En espera», sin entregar, cancelar ni devolver; incluye los que siguen ahí de días anteriores."
            metrics={<span>{shownCount(depotShown.length, depotShipments.length)} envíos</span>}
          >
            <TypeFilter
              label="Tipo de envío en depósito"
              shipments={depotShipments}
              value={depotType}
              onChange={setDepotType}
            />
            <TowerShipmentList
              shipments={depotShown}
              showZone
              operationalDateLabel={operationalDateLabel}
              emptyText={
                !depotShipments.length
                  ? "No hay envíos en el depósito."
                  : ofType(depotShipments, depotType).length
                    ? "No hay envíos del depósito que coincidan con la búsqueda."
                    : "No hay envíos de este tipo en el depósito."
              }
            />
          </TowerSection>

          {unclosedShipments.length > 0 && (
            <TowerSection
              id="tower-unclosed"
              className="tower-zone-unclosed"
              open={openSections.unclosed || false}
              onToggle={setSectionOpen("unclosed")}
              title="Sin cerrar en Lightdata · días anteriores"
              help="Siguen «En camino» o pendientes en Lightdata sin movimiento desde antes del último día de reparto. No suman en los conteos: hay que cerrarlos en Lightdata."
              metrics={
                <span>
                  <b className="tower-number-flex">{ofType(unclosedShipments, "FLEX").length}</b> Flex ·{" "}
                  {unclosedShipments.length} envíos
                </span>
              }
            >
              <TypeFilter
                label="Tipo de envío sin cerrar"
                shipments={unclosedShipments}
                value={unclosedType}
                onChange={setUnclosedType}
              />
              <TowerShipmentList
                shipments={unclosedShown}
                showZone
                operationalDateLabel={operationalDateLabel}
                emptyText={
                  ofType(unclosedShipments, unclosedType).length
                    ? "No hay envíos sin cerrar que coincidan con la búsqueda."
                    : "No hay envíos de este tipo sin cerrar."
                }
              />
            </TowerSection>
          )}

          {reviewShipments.length > 0 && (
            <>
              <div className="tower-location-review">
                <h3 title="La localidad no coincide con una regla segura y el código postal no alcanza para decidir la zona (falta o se reparte entre zonas).">
                  Ubicaciones por revisar <span className="tower-muted">{reviewShipments.length} envíos</span>
                </h3>
              </div>
              <TowerSection
                className="tower-zone-review"
                open={openSections.review || false}
                onToggle={setSectionOpen("review")}
                title="Envíos sin zona"
                help="Tocá un envío para abrir el detalle de su chofer."
                metrics={<span>{shownCount(reviewShown.length, reviewShipments.length)} envíos</span>}
              >
                <TowerShipmentList
                  shipments={reviewShown}
                  showZone={false}
                  operationalDateLabel={operationalDateLabel}
                  emptyText="No hay envíos por revisar que coincidan con la búsqueda."
                  onSelectDriver={onSelectDriver}
                />
              </TowerSection>
            </>
          )}
        </Card>
      </section>
    </>
  );
}
